import { CheckCircle2, Download, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { downloadMedia } from "@/lib/mediaUtils";
import { PreviewSkeletonTile } from "@/components/flowly/PreviewSkeletonTile/PreviewSkeletonTile";
import { CreditCostPill } from "@/components/flowly/CreditCostPill/CreditCostPill";

interface BaseballTrendPreviewGridProps {
  /** Public R2 URLs of the N broadcast previews. Empty until the user
   *  triggers a generation. */
  imageUrls: string[];
  selectedIndex: number | null;
  isGenerating: boolean;
  /** ISO timestamp captured when the active generation started — drives the
   *  progress bar's elapsed-time curve. Required while `isGenerating`. */
  generationStartedAt: string | null;
  /** How many slots to render when generating with no images yet. */
  slots: number;
  onSelect: (index: number) => void;
  onRegenerate: () => void;
  /** Regenerate is disabled when the user hasn't generated once yet OR a
   *  generation is already in flight. */
  canRegenerate: boolean;
  /** Credit cost of one regenerate click — shown next to the label so the
   *  regenerate CTA mirrors the initial "Generate 2 previews · N" button. */
  previewCost: number;
}

/** Step 4 picker — user chooses one of N broadcast previews to animate.
 *  Mirrors the Talking Avatar preview UX exactly: 2-column grid, animated
 *  pointer hint while both are ready and nothing is picked yet. */
export function BaseballTrendPreviewGrid({
  imageUrls,
  selectedIndex,
  isGenerating,
  generationStartedAt,
  slots,
  onSelect,
  onRegenerate,
  canRegenerate,
  previewCost,
}: BaseballTrendPreviewGridProps) {
  const { t } = useLanguage();

  // Cursor hint only when both previews are ready AND user hasn't picked yet.
  const showCursorHint = imageUrls.length === slots && selectedIndex === null;
  const pendingSlots = isGenerating && imageUrls.length === 0 ? slots : 0;

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.baseballTrend.previewTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.baseballTrend.previewHint}
        </p>
      </div>

      <div className="relative grid grid-cols-2 gap-3 md:gap-4 max-w-md mx-auto">
        {imageUrls.map((url, idx) => {
          const isSelected = selectedIndex === idx;
          return (
            <div key={url} className="relative">
              <button
                type="button"
                onClick={() => onSelect(idx)}
                aria-pressed={isSelected}
                className={cn(
                  "relative aspect-[4/5] w-full rounded-2xl overflow-hidden border-2 transition-all duration-200",
                  isSelected
                    ? "border-orange-500 ring-2 ring-orange-500/40 shadow-md"
                    : "border-border hover:border-orange-500 hover:ring-2 hover:ring-orange-500/40 hover:shadow-sm md:hover:-translate-y-0.5",
                )}
              >
                <img
                  src={url}
                  alt={`preview ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {isSelected && (
                  <div className="absolute top-2 end-2 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void downloadMedia(url, "baseball-trend-preview", "png");
                }}
                aria-label={t.baseballTrend.previewDownloadAria}
                className="absolute bottom-2 start-2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          );
        })}

        {generationStartedAt &&
          Array.from({ length: pendingSlots }).map((_, i) => (
            <PreviewSkeletonTile
              key={`pending-${i}`}
              startTime={generationStartedAt}
              aspectClass="aspect-[4/5]"
            />
          ))}

        {showCursorHint && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none flex items-center justify-center"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-8 h-8 md:w-10 md:h-10 drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)] animate-pick-hint"
            >
              <path
                d="M4 4l7.07 17 2.51-7.39L21 11.07 4 4z"
                fill="white"
                stroke="black"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      {imageUrls.length > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={!canRegenerate}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors",
              "bg-card border border-border hover:border-primary/40 hover:bg-card/80",
              !canRegenerate && "opacity-50 cursor-not-allowed",
            )}
          >
            <RefreshCw className={cn("w-4 h-4", isGenerating && "animate-spin")} />
            <span>{t.baseballTrend.previewRegenerate}</span>
            <CreditCostPill cost={previewCost} />
          </button>
        </div>
      )}
    </div>
  );
}